import {
  Component,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { AuthService } from './services/auth.service';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { filter, firstValueFrom, Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectAuthStatus, selectClientInfo } from './store/auth/auth.reducer';
import { AuthStatus, IClientInfo } from './models/authentication';
import { config } from '../config/appConfig';
import { FetchClientInfoAction, LogoutAction } from './store/auth/auth.actions';
import { IUserInfo } from './models/user';
import { selectIsNewUser, selectUserInfo } from './store/user/user.reducer';
import { UserService } from './services/user.service';
import { AuthPaths } from './modules/authentication/authentication-routing.module';
import { CorePaths } from './modules/core/core-routing.module';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { UserPermissions } from './_helpers/permissions.enum';
import { MediaDeliveryService } from './services/mediaDelivery.services';
import { SubscriptionTabs } from './modules/core/subscription/subscription.component';
import {
  HelpPageURL,
  PrivacyPolicyURL,
  TermsOfServiceURL,
} from '../resources/common.resources';
import { ParentalGateSessionService } from './services/parentalGateSession.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('sidebar') sidebar!: ElementRef;
  @ViewChild('copyrightContent') copyrightContent!: ElementRef;
  isLoggedIn: boolean = false;
  isNewUser: boolean = true;
  userInfo: IUserInfo | null = null;
  book: [string, string] | null = null;
  chapter: [string, string] | null = null;
  subchapter: [string, string] | null = null;
  page: [string, string] | null = null;
  get permissions(): string[] {
    let permissions: string[] = this.userInfo
      ? [...new Set(this.userInfo.Roles.flatMap((role) => role.Permissions))]
      : [];
    if (permissions.filter((t) => t == '*').length > 0) {
      permissions = Object.values(UserPermissions);
    }
    return permissions;
  }
  isCollapsed: boolean = true;

  DEFAULT_PROFILE_IMAGE: string = 'assets/images/default-profile.png';
  safePictureUrl: SafeUrl | string = this.DEFAULT_PROFILE_IMAGE;

  serviceURLS = {
    termsOfServiceURL: TermsOfServiceURL,
    privacyPolicyURL: PrivacyPolicyURL,
    helpURL: HelpPageURL,
  };

  // Observables
  authStatus$: Observable<AuthStatus>;
  userInfo$: Observable<IUserInfo | null>;
  isNewUser$: Observable<boolean>;
  clientInfo$: Observable<IClientInfo | null>;
  private isResizing = false;
  private sideBarMinWidth = 250;
  private sideBarMaxWidth = 600;
  private isCustomer: boolean = true;
  disableNavButtons: boolean = false;
  private _pendingState: any = {};

  defaultRoute: string = config.defaultRoute;
  private _subscriptions: Subscription[] = [];

  SIDEBAR_STATUS: string = 'SIDEBAR_STATUS';

  menuOptions = [
    { label: 'Home', icon: 'Home_Icon.svg', route: '/books' },
    {
      label: 'Manage Orders',
      icon: 'Manage_Orders_Icon.svg',
      route: '/admin/orders',
      permissionRequired: UserPermissions.ORDER_VIEW_ALL,
    },
    {
      label: 'Customers Report',
      icon: 'Customer_Reports_Icon.svg',
      route: '/admin/userreports',
      permissionRequired: UserPermissions.USERREPORT_READ,
    },
    {
      label: 'Organizations',
      icon: 'Organizations_Icon.svg',
      route: '/sales/organizations',
      permissionRequired: UserPermissions.ORGANIZATION_READ,
    },
    {
      label: 'Admin Settings',
      icon: 'Admin_Settings.svg',
      route: '/admin/settings',
      permissionRequired: UserPermissions.BOOK_ACCESS_MANAGE,
    },
    {
      label: 'Reports',
      icon: 'Reports_Icon.svg',
      route: '/admin/gstreport',
      permissionRequired: UserPermissions.REPORT_READ,
    },
    {
      label: 'Books',
      icon: 'Books_Icon.svg',
      route: '/admin/books',
      permissionRequired: 'book.read',
    },
    {
      label: 'BooksTree',
      icon: 'GiveGift.svg',
      route: '',
      permissionRequired: 'book.read',
    },
  ];

  // Add the "route" paramter to make it active
  dropdownOptions = [
    {
      label: 'My Profile',
      action: () => this.viewProfile(),
      route: CorePaths.profile,
    },
    {
      label: 'My Subscription',
      action: () => this.viewSubscription(),
      route: CorePaths.subscription,
    },
    {
      label: 'Help & Support',
      action: () => window.open(this.serviceURLS.helpURL, '_blank'),
    },
    {
      label: 'Terms of Service',
      action: () => window.open(this.serviceURLS.termsOfServiceURL, '_blank'),
    },
    {
      label: 'Privacy Policy',
      action: () => window.open(this.serviceURLS.privacyPolicyURL, '_blank'),
    },
    { label: 'Logout', action: () => this.store.dispatch(LogoutAction({})) },
  ];

  //Method to check if a dropdown item is active
  isDropdownItemActive(option: any): boolean {
    if (!option.route) return false;
    return this.router.url.includes(option.route);
  }

  onDropdownItemClick(option: any) {
    if (option.action) {
      option.action();
    }
  }

  viewSubscription() {
    this.router.navigate([CorePaths.subscription], {
      queryParams: { tab: SubscriptionTabs.PLANS },
    });
  }

  viewProfile() {
    // Navigate To Profile Page
    this.router.navigate([CorePaths.profile]);
  }

  constructor(
    private authService: AuthService,
    public router: Router,
    private route: ActivatedRoute,
    private store: Store,
    private userService: UserService,
    private sanitizer: DomSanitizer,
    private mediaDeliveryService: MediaDeliveryService,
    private parentalGateSession: ParentalGateSessionService,
  ) {
    this.authStatus$ = this.store.select(selectAuthStatus);
    this.userInfo$ = this.store.select(selectUserInfo);
    this.isNewUser$ = this.store.select(selectIsNewUser);
    this.clientInfo$ = this.store.select(selectClientInfo);

    // overridden the console.error and console.log method to hide the unnecessary error msg and logs
    // added for ngx-daterangepicker bug - https://github.com/fetrarij/ngx-daterangepicker-material/issues/514
    // #554 gitlab issue
    // bug has been fixed on github, but latest version not released on npm
    // this doesn't break any functioning, just a console error appears, which is now hidden using this code
    const originalError = console.error;

    console.error = (...args) => {
      if (
        args.some(
          (a) =>
            a
              .toString()
              .includes(
                "Cannot read properties of undefined (reading 'selectedHour')",
              ) && a?.stack?.toString().includes('getDateWithTime'),
        )
      ) {
        return;
      }

      originalError.apply(console, args);
    };

    const originalLog = console.log;

    console.log = (...args: any[]) => {
      if (args.length && typeof args[0] === 'string' && args[0] === '1 ') {
        return;
      }

      originalLog.apply(console, args);
    };
    // //
    //
  }

  navigateToClickTarget(menuItem: (typeof this.menuOptions)[0]) {
    if (!this.disableNavButtons && menuItem.route) {
      this.router.navigate([menuItem.route]);
    }
  }

  logout() {
    this.store.dispatch(LogoutAction({}));
    setTimeout(() => {
      this.updateCopyrightPosition();
    }, 200);
  }

  /**
   * The menu item for this page, or for a page inside it - e.g. Organizations
   * stays highlighted on an organization's own page and its users page.
   * Whole path segments only, so '/admin/books' does not claim '/admin/booksx'.
   */
  isActiveRoute(route: string): boolean {
    if (!route) return false;
    const path = this.router.url.split(/[?#]/)[0];

    return path === route || path.startsWith(`${route}/`);
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    localStorage.setItem(this.SIDEBAR_STATUS, JSON.stringify(this.isCollapsed));
    setTimeout(() => {
      this.updateCopyrightPosition();
    }, 200);
  }

  onImageError(event: Event) {
    this.safePictureUrl = this.DEFAULT_PROFILE_IMAGE;
  }

  private extractSegmentAfter(url: string, key: string): string | null {
    const parts = url.split('/');
    const index = parts.indexOf(key);
    return index !== -1 && parts.length > index + 1 ? parts[index + 1] : null;
  }

  // Resizing Of the SideNav Bar
  startResize(event: MouseEvent) {
    if (this.isCollapsed) return; // Disable resizing when collapsed
    this.isResizing = true;
    document.addEventListener('mousemove', (e) => this.resize(e));
    document.addEventListener('mouseup', (e) => this.stopResize());
    event.preventDefault();
  }

  resize(event: MouseEvent) {
    if (!this.isResizing) return;
    const sidebar = this.sidebar.nativeElement;
    const newWidth = event.clientX - sidebar.getBoundingClientRect().left;
    if (newWidth >= this.sideBarMinWidth && newWidth <= this.sideBarMaxWidth) {
      sidebar.style.width = `${newWidth}px`;
      this.updateCopyrightPosition(newWidth);
    }
  }

  stopResize() {
    this.isResizing = false;
    document.removeEventListener('mousemove', (e) => this.resize(e));
    document.removeEventListener('mouseup', (e) => this.stopResize);
  }

  private updateCopyrightPosition(width?: number) {
    return;
    if (!this.copyrightContent) return;
    const sidebar = this.sidebar?.nativeElement;
    if (this.isLoggedIn && !this.isNewUser) {
      const sidebarWidth =
        width ?? sidebar?.offsetWidth ?? this.sideBarMinWidth;
      this.copyrightContent.nativeElement.style.left = this.isCollapsed
        ? '80px'
        : `${sidebarWidth}px`;
    } else {
      this.copyrightContent.nativeElement.style.left = '10px';
    }
  }

  get showExpandSideBarControl(): boolean {
    return !this.isCustomer;
  }

  // Condition To Dynamically Disable the Navigation Buttons
  toDisableNavigationButtons(): boolean {
    const url = this.router.url;

    const isPlansPage =
      url === '/subscriptions?tab=plans' ||
      url.startsWith('/subscriptions') ||
      url.startsWith('/transactions');

    const showDialog =
      this.router.getCurrentNavigation()?.extras?.state?.[
        'showSubscriptionAsDialog'
      ] ??
      this._pendingState['showSubscriptionAsDialog'] ??
      false;

    return isPlansPage && showDialog;
  }

  ngOnInit(): void {
    // Fetch the ClientInfo Action
    this.store.dispatch(FetchClientInfoAction());

    // Auth Status
    this._subscriptions.push(
      this.authStatus$.subscribe((authStatus) => {
        this.isLoggedIn = authStatus === AuthStatus.LoggedIn;
      }),
    );

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const nav = event as NavigationEnd;
        const url = nav.urlAfterRedirects;
        const bookId = this.extractSegmentAfter(url, 'books');
        const chapterId = this.extractSegmentAfter(url, 'chapters');
        const subchapterId = this.extractSegmentAfter(url, 'subchapters');

        this.book = bookId ? ['book', bookId] : null;
        this.chapter = chapterId ? ['chapter', chapterId] : null;
        this.subchapter = subchapterId ? ['subchapter', subchapterId] : null;

        // Update the CopyRight Position
        setTimeout(() => {
          this.updateCopyrightPosition();
        }, 200);

        // Disable Enable Nav Buttons Conditionally When Route Changes
        this.disableNavButtons = this.toDisableNavigationButtons();
      });

    this.router.events
      .pipe(filter((e) => e instanceof NavigationStart))
      .subscribe((e: NavigationStart) => {
        // store the state for the guard
        this._pendingState = (e as any).extras?.state ?? {};
      });

    this._subscriptions.push(
      this.userInfo$.pipe(filter(Boolean)).subscribe(async (userInfo) => {
        this.userInfo = await firstValueFrom(
          this.store.select(selectUserInfo).pipe(filter(Boolean)),
        );

        this.isCustomer =
          this.userInfo?.Roles?.some((role) =>
            ['FreeUser', 'StandardUser', 'PremiumUser'].includes(role.RoleName),
          ) || false;

        if (this.isCustomer) {
          this.isCollapsed = true;
        } else {
          const storedState = localStorage.getItem(this.SIDEBAR_STATUS);
          this.isCollapsed = storedState ? JSON.parse(storedState) : true;
        }

        if (this.userInfo?.PictureUrl) {
          this.safePictureUrl = this.mediaDeliveryService.getFullUrl(
            this.userInfo.PictureUrl,
          );
        } else {
          this.safePictureUrl = this.DEFAULT_PROFILE_IMAGE;
        }

        if (!this.isLoggedIn) return;

        this.isNewUser = this.userService.checkIfNewUser();
        if (this.isNewUser) {
          this.router.navigate([AuthPaths.register]);
          return;
        }

        // Extract the route path without leading '/'
        const currentRoute = this.router.url.startsWith('/')
          ? this.router.url.slice(1)
          : this.router.url;

        if (
          [AuthPaths.signIn, AuthPaths.register].includes(
            currentRoute as AuthPaths,
          )
        ) {
          this.router.navigate([this.defaultRoute]);
          return;
        }

        // Do Not Delete The Next Line
        // this.router.navigate([this.router.url !== '/' ? this.router.url : this.defaultRoute]);
        return;
      }),
    );

    // Update User
    this._subscriptions.push(
      this.isNewUser$.subscribe(async (isNewUser) => {
        this.isNewUser = isNewUser;

        this.userInfo = (await firstValueFrom(
          this.store
            .select(selectUserInfo)
            .pipe(filter((val: any) => val != null)),
        )) as IUserInfo;

        if (this.userInfo?.PictureUrl) {
          this.safePictureUrl = this.mediaDeliveryService.getFullUrl(
            this.userInfo.PictureUrl,
          );
        } else {
          this.safePictureUrl = this.DEFAULT_PROFILE_IMAGE;
        }
      }),
    );

    // Subscribe to the Client Info
    this._subscriptions.push(
      this.clientInfo$.subscribe((clientInfo) => {
        if (clientInfo) {
          const shouldAllowLogin = Object.values(
            clientInfo.LoginMethodsAllowed,
          ).some((method) => method === true);
          if (!shouldAllowLogin) {
            // If No Login Methods are allowed, then logout the user
            this.store.dispatch(LogoutAction({}));
          }
        }
      }),
    );
  }

  ngAfterViewInit(): void {
    this.initializeSidebarBehavior();
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private initializeSidebarBehavior(): void {
    // Toggle collapse/expand sidebar
    const toggleButton = document.querySelector(
      '[data-toggle="sidebar-colapse"]',
    );
    const sidebarContainer = document.getElementById('sidebar-container');
    const collapseIcon = document.getElementById('collapse-icon');

    if (toggleButton && sidebarContainer && collapseIcon) {
      toggleButton.addEventListener('click', () => {
        sidebarContainer.classList.toggle('sidebar-expanded');
        sidebarContainer.classList.toggle('sidebar-collapsed');

        collapseIcon.classList.toggle('fa-angle-double-left');
        collapseIcon.classList.toggle('fa-angle-double-right');

        // Toggle visibility of submenu and icons
        document
          .querySelectorAll('.menu-collapsed, .sidebar-submenu, .submenu-icon')
          .forEach((element) => element.classList.toggle('d-none'));
      });
    }
  }
}
